import React, { useState, useEffect, useRef } from 'react';
import { Plus, Eye, Anchor, CheckCircle, Circle, Trash2, Search, Filter, X, Edit2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PortCalls = () => {
  const [portCalls, setPortCalls] = useState([]);
  const [filteredPortCalls, setFilteredPortCalls] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [clients, setClients] = useState([]);
  const [ports, setPorts] = useState([]); // Liman listesi
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedPortCall, setSelectedPortCall] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPortCall, setEditingPortCall] = useState(null);
  
  // Filtreleme state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [vesselFilter, setVesselFilter] = useState('all');
  
  // Aranabilir dropdown state'leri
  const [originPortSearch, setOriginPortSearch] = useState('');
  const [destPortSearch, setDestPortSearch] = useState('');
  const [showOriginPortDropdown, setShowOriginPortDropdown] = useState(false);
  const [showDestPortDropdown, setShowDestPortDropdown] = useState(false);
  
  // Demirsahası seçenekleri - settings'den yüklenecek
  const [anchorageOptions, setAnchorageOptions] = useState(['1. Demirsahası', '2. Demirsahası', '3. Demirsahası']);
  
  const [formData, setFormData] = useState({
    vessel_id: '',
    vessel_name: '',
    client_id: '',
    client_name: '',
    proforma_id: '',
    pda_number: '',
    origin_port: '',
    origin_port_code: '',
    destination_port: '',
    destination_port_code: '',
    location_type: 'anchorage',
    berth: '',
    eta: '',
    etd: '',
    notes: ''
  });

  const [editFormData, setEditFormData] = useState({
    location_type: 'anchorage',
    berth: '',
    eta: '',
    etd: '',
    notes: ''
  });

  const [proformas, setProformas] = useState([]);

  // Dropdown dışına tıklandığında kapatma
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.form-group')) {
        setShowOriginPortDropdown(false);
        setShowDestPortDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // Filtreleme effect'i
  useEffect(() => {
    filterPortCalls();
  }, [portCalls, searchTerm, statusFilter, locationFilter, vesselFilter]);

  const loadData = async () => {
    try {
      const [portCallsRes, vesselsRes, clientsRes, settingsRes, proformasRes] = await Promise.all([
        api.get('/port-calls'),
        api.get('/vessels'),
        api.get('/clients'),
        api.get('/settings'),
        api.get('/proformas')
      ]);
      setPortCalls(portCallsRes.data);
      setVessels(vesselsRes.data);
      setClients(clientsRes.data);
      // Yeni port sistemi - flat port list
      if (settingsRes.data?.ports) {
        setPorts(settingsRes.data.ports);
      }
      // Demirsahası seçenekleri - motor_launch_services'dan al
      if (settingsRes.data?.motor_launch_services) {
        const anchorages = settingsRes.data.motor_launch_services
          .filter(s => s.active)
          .map(s => s.name_tr);
        if (anchorages.length > 0) {
          setAnchorageOptions(anchorages);
        }
      }
      setProformas(proformasRes.data);
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Filtreleme fonksiyonu
  const filterPortCalls = () => {
    let filtered = [...portCalls];

    // Arama filtresi
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pc =>
        pc.vessel_name?.toLowerCase().includes(term) ||
        pc.client_name?.toLowerCase().includes(term) ||
        pc.number?.toLowerCase().includes(term) ||
        pc.berth?.toLowerCase().includes(term) ||
        pc.origin_port?.toLowerCase().includes(term) ||
        pc.destination_port?.toLowerCase().includes(term)
      );
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pc => pc.status === statusFilter);
    }

    // Konum tipi filtresi
    if (locationFilter !== 'all') {
      filtered = filtered.filter(pc => pc.location_type === locationFilter);
    }

    // Gemi filtresi
    if (vesselFilter !== 'all') {
      filtered = filtered.filter(pc => pc.vessel_id === vesselFilter);
    }

    setFilteredPortCalls(filtered);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLocationFilter('all');
    setVesselFilter('all');
  };

  // Port call için onaylı proforma numarasını bul
  const getApprovedProformaNumber = (portCall) => {
    // Önce proforma_ids ile dene
    if (portCall.proforma_ids && portCall.proforma_ids.length > 0) {
      const approvedProforma = proformas.find(p => 
        portCall.proforma_ids.includes(p.id) && p.status === 'approved'
      );
      if (approvedProforma?.number) return approvedProforma.number;
    }
    
    // proforma_ids yoksa veya boşsa, port_call_id üzerinden dene
    const proformaByPortCallId = proformas.find(p => 
      p.port_call_id === portCall.id && p.status === 'approved'
    );
    if (proformaByPortCallId?.number) return proformaByPortCallId.number;
    
    // Hiçbiri yoksa, gemi adı ve müşteri adına göre eşleştir
    const proformaByVessel = proformas.find(p => 
      p.vessel_name === portCall.vessel_name && 
      p.client_name === portCall.client_name &&
      p.status === 'approved'
    );
    
    return proformaByVessel?.number || null;
  };

  const refreshPortsData = async () => {
    try {
      const settingsRes = await api.get('/settings');
      if (settingsRes.data?.ports) {
        setPorts(settingsRes.data.ports);
      }
    } catch (error) {
      console.error('Ports data refresh failed:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        eta: formData.eta ? new Date(formData.eta).toISOString() : null,
        etd: formData.etd ? new Date(formData.etd).toISOString() : null,
        // Proforma_id boşsa undefined yap (API'ye gönderilmesin)
        proforma_id: formData.proforma_id || undefined
      };
      
      // Boş alanları temizle
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null) {
          delete submitData[key];
        }
      });
      
      await api.post('/port-calls', submitData);
      toast.success('Liman çağrısı başarıyla oluşturuldu');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Liman çağrısı oluşturulamadı');
    }
  };

  const resetForm = () => {
    setFormData({
      vessel_id: '',
      vessel_name: '',
      client_id: '',
      client_name: '',
      proforma_id: '',
      pda_number: '',
      origin_port: '',
      origin_port_code: '',
      destination_port: '',
      destination_port_code: '',
      location_type: 'anchorage',
      berth: '',
      eta: '',
      etd: '',
      notes: ''
    });
    // Arama alanlarını da sıfırla
    setOriginPortSearch('');
    setDestPortSearch('');
  };

  const handleViewDetail = async (portCall) => {
    try {
      const response = await api.get(`/port-calls/${portCall.id}`);
      setSelectedPortCall(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Liman çağrısı detayları yüklenemedi');
    }
  };

  const handleDelete = async (portCallId, portCallNumber) => {
    if (!window.confirm(`"${portCallNumber}" liman çağrısını silmek istediğinizden emin misiniz?`)) return;
    
    try {
      await api.delete(`/port-calls/${portCallId}`);
      toast.success('Liman çağrısı başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Liman çağrısı silinemedi');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await api.put(`/port-calls/${selectedPortCall.id}/status`, { status: newStatus });
      toast.success('Durum güncellendi');
      loadData();
      const response = await api.get(`/port-calls/${selectedPortCall.id}`);
      setSelectedPortCall(response.data);
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleEdit = (portCall) => {
    setEditingPortCall(portCall);
    setEditFormData({
      pda_number: portCall.pda_number || '',
      location_type: portCall.location_type || 'berth',
      berth: portCall.berth || '',
      eta: portCall.eta ? portCall.eta.slice(0, 16) : '',
      etd: portCall.etd ? portCall.etd.slice(0, 16) : '',
      notes: portCall.notes || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Boş string'leri null'a çevir
      const submitData = {
        ...editFormData,
        eta: editFormData.eta || null,
        etd: editFormData.etd || null,
        pda_number: editFormData.pda_number || null,
        berth: editFormData.berth || null,
        notes: editFormData.notes || null
      };
      await api.put(`/port-calls/${editingPortCall.id}`, submitData);
      toast.success('Liman çağrısı güncellendi');
      loadData();
      setShowEditModal(false);
      setEditingPortCall(null);
      // Detail modal açıksa güncelle
      if (selectedPortCall && selectedPortCall.id === editingPortCall.id) {
        const response = await api.get(`/port-calls/${editingPortCall.id}`);
        setSelectedPortCall(response.data);
      }
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleChecklistUpdate = async (updatedChecklist) => {
    try {
      await api.put(`/port-calls/${selectedPortCall.id}/checklist`, { checklist: updatedChecklist });
      toast.success('Kontrol listesi güncellendi');
      const response = await api.get(`/port-calls/${selectedPortCall.id}`);
      setSelectedPortCall(response.data);
    } catch (error) {
      toast.error('Kontrol listesi güncellenemedi');
    }
  };

  const toggleChecklistItem = (index) => {
    const updated = [...selectedPortCall.checklist];
    updated[index].completed = !updated[index].completed;
    if (updated[index].completed) {
      updated[index].completed_at = new Date().toISOString();
    } else {
      updated[index].completed_at = null;
    }
    handleChecklistUpdate(updated);
  };

  // Fatura kesildi kontrolü - checklist'te "Fatura Kesildi" veya "Invoice" içeren item completed mi?
  const isInvoiceIssued = (portCall) => {
    if (!portCall.checklist) return false;
    return portCall.checklist.some(item => 
      (item.task.toLowerCase().includes('fatura') || item.task.toLowerCase().includes('invoice')) && 
      item.completed
    );
  };

  // Checklist tamamlanmış mı kontrolü (tüm itemlar completed)
  const isChecklistComplete = (portCall) => {
    if (!portCall.checklist || portCall.checklist.length === 0) return true;
    return portCall.checklist.every(item => item.completed);
  };

  // Sailed durumundaki port call'ları ayır
  const getSailedWithPendingTasks = () => {
    return portCalls.filter(p => p.status === 'sailed' && !isInvoiceIssued(p));
  };

  const getSailedCompleted = () => {
    return portCalls.filter(p => p.status === 'sailed' && isInvoiceIssued(p));
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'badge-draft',
      created: 'badge-draft',
      eta_confirmed: 'badge-sent',
      arrived: 'badge-approved',
      in_port: 'badge-partial',
      sailed: 'badge-paid',
      cancelled: 'badge-cancelled'
    };
    return classes[status] || 'badge-draft';
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      draft: 'created',
      created: 'eta_confirmed',
      eta_confirmed: 'arrived',
      arrived: 'in_port',
      in_port: 'sailed'
    };
    return statusFlow[currentStatus];
  };

  // Önceki duruma dönme
  const getPreviousStatus = (currentStatus) => {
    const reverseFlow = {
      created: 'draft',
      eta_confirmed: 'created',
      arrived: 'eta_confirmed',
      in_port: 'arrived',
      sailed: 'in_port'
    };
    return reverseFlow[currentStatus];
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Taslak',
      created: 'Oluşturuldu',
      eta_confirmed: 'ETA Onaylandı',
      arrived: 'Vardı',
      in_port: 'Limanda',
      sailed: 'Ayrıldı',
      cancelled: 'İptal'
    };
    return labels[status] || status;
  };

  // Port Call tablosu render fonksiyonu
  const renderPortCallTable = (portCallList, title, bgColor) => {
    if (portCallList.length === 0) return null;
    
    return (
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0.75rem 1rem', 
          backgroundColor: bgColor, 
          color: 'white',
          borderRadius: '8px 8px 0 0',
          fontWeight: '600'
        }}>
          <span>{title}</span>
          <span style={{ 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '12px',
            fontSize: '0.875rem'
          }}>
            {portCallList.length}
          </span>
        </div>
        <div className="table-container" style={{ borderRadius: '0 0 8px 8px', border: `1px solid ${bgColor}`, borderTop: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>PDA No</th>
                <th>Gemi</th>
                <th>Müşteri</th>
                <th>Konum</th>
                <th>Varış Zamanı</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {portCallList.map((pc) => (
                <tr key={pc.id} data-testid={`portcall-row-${pc.id}`}>
                  <td>
                    {pc.pda_number ? (
                      <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600 }}>
                        {pc.pda_number}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>-</span>
                    )}
                  </td>
                  <td><strong>{pc.vessel_name}</strong></td>
                  <td>{pc.client_name}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: pc.location_type === 'anchorage' ? '#fef3c7' : '#dbeafe',
                        color: pc.location_type === 'anchorage' ? '#b45309' : '#1d4ed8'
                      }}>
                        {pc.location_type === 'anchorage' ? 'Demirsahası' : 'Rıhtım'}
                      </span>
                      {pc.berth && <span>{pc.berth}</span>}
                    </div>
                  </td>
                  <td>{formatDateTime(pc.eta)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="action-btn" onClick={() => handleViewDetail(pc)} title="Detay"><Eye size={18} /></button>
                      <button className="action-btn action-btn-edit" onClick={() => handleEdit(pc)} title="Düzenle"><Edit2 size={18} /></button>
                      <button className="action-btn action-btn-danger" onClick={() => handleDelete(pc.id, pc.number)} title="Sil"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">Liman Çağrıları</h1>
        <button className="btn btn-primary" onClick={() => { refreshPortsData(); setShowModal(true); }} data-testid="add-portcall-btn">
          <Plus size={18} />
          Yeni Liman Çağrısı
        </button>
      </div>
      
      <div style={{ padding: '2rem' }}>
        {/* Filtreleme Bölümü */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-body" style={{ padding: '1rem 1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' }}>
              {/* Arama */}
              <div style={{ position: 'relative', width: '220px' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  style={{ paddingLeft: '32px', height: '34px', fontSize: '0.85rem' }}
                  data-testid="search-input"
                />
              </div>

              {/* Durum Filtresi */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
                style={{ height: '34px', width: '120px', fontSize: '0.85rem', padding: '0 0.5rem' }}
                data-testid="status-filter"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="draft">Taslak</option>
                <option value="created">Oluşturuldu</option>
                <option value="eta_confirmed">ETA Onaylı</option>
                <option value="arrived">Geldi</option>
                <option value="in_port">Limanda</option>
                <option value="sailed">Ayrıldı</option>
                <option value="cancelled">İptal</option>
              </select>

              {/* Konum Tipi Filtresi */}
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="form-input"
                style={{ height: '34px', width: '115px', fontSize: '0.85rem', padding: '0 0.5rem' }}
                data-testid="location-filter"
              >
                <option value="all">Tüm Konumlar</option>
                <option value="berth">Rıhtım</option>
                <option value="anchorage">Demirsahası</option>
              </select>

              {/* Gemi Filtresi */}
              <select
                value={vesselFilter}
                onChange={(e) => setVesselFilter(e.target.value)}
                className="form-input"
                style={{ height: '34px', width: '130px', fontSize: '0.85rem', padding: '0 0.5rem' }}
                data-testid="vessel-filter"
              >
                <option value="all">Tüm Gemiler</option>
                {vessels.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>

              {/* Filtreleri Temizle */}
              {(searchTerm || statusFilter !== 'all' || locationFilter !== 'all' || vesselFilter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="btn btn-secondary"
                  style={{ height: '34px', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', padding: '0 0.5rem' }}
                  data-testid="clear-filters-btn"
                >
                  <X size={14} />
                  Temizle
                </button>
              )}
            </div>

            {/* Sonuç sayısı */}
            <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#64748b' }}>
              {filteredPortCalls.length} / {portCalls.length} liman çağrısı gösteriliyor
            </div>
          </div>
        </div>

        {loading ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div></div>
        ) : portCalls.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-state-icon"><Anchor /></div>
                <h3 className="empty-state-title">Henüz liman çağrısı yok</h3>
                <p className="empty-state-text">İlk liman çağrınızı oluşturun</p>
                <button className="btn btn-primary" onClick={() => { refreshPortsData(); setShowModal(true); }}>
                  <Plus size={18} />
                  Liman Çağrısı Oluştur
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Özet Kartları */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ background: 'linear-gradient(135deg, #94a3b8, #64748b)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{portCalls.filter(p => p.status === 'draft' || p.status === 'created' || p.status === 'eta_confirmed').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Bekleyen</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{portCalls.filter(p => p.status === 'arrived').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Vardı</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{portCalls.filter(p => p.status === 'in_port').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Limanda</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{getSailedWithPendingTasks().length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Eksik İşlem</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{getSailedCompleted().length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Tamamlandı</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{portCalls.filter(p => p.status === 'cancelled').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>İptal</div>
              </div>
            </div>

            {/* Duruma Göre Gruplandırılmış Tablolar */}
            {renderPortCallTable(portCalls.filter(p => p.status === 'draft' || p.status === 'created' || p.status === 'eta_confirmed'), '⏳ Bekleyen Çağrılar', '#94a3b8')}
            {renderPortCallTable(portCalls.filter(p => p.status === 'arrived'), '🚢 Vardı', '#f59e0b')}
            {renderPortCallTable(portCalls.filter(p => p.status === 'in_port'), '⚓ Limanda', '#3b82f6')}
            {renderPortCallTable(getSailedWithPendingTasks(), '⚠️ Ayrıldı - Eksik İşlem', '#f97316')}
            {renderPortCallTable(getSailedCompleted(), '✅ Ayrıldı - Tamamlandı', '#10b981')}
            {renderPortCallTable(portCalls.filter(p => p.status === 'cancelled'), '❌ İptal Edilen', '#ef4444')}
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="modal-title">Liman Çağrısı Oluştur</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Gemi *</label>
                    <select className="form-select" value={formData.vessel_id} onChange={(e) => {
                      const selectedVessel = vessels.find(v => v.id === e.target.value);
                      setFormData({ ...formData, vessel_id: e.target.value, vessel_name: selectedVessel?.name || '' });
                    }} required data-testid="vessel-select">
                      <option value="">Select Vessel</option>
                      {vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>{vessel.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Müşteri *</label>
                    <select className="form-select" value={formData.client_id} onChange={(e) => {
                      const selectedClient = clients.find(c => c.id === e.target.value);
                      setFormData({ ...formData, client_id: e.target.value, client_name: selectedClient?.name || '' });
                    }} required data-testid="client-select">
                      <option value="">Select Client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Proforma Seçimi - Opsiyonel */}
                <div className="form-group">
                  <label className="form-label">Proforma <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal' }}>(Opsiyonel - onaylı proforma seçebilirsiniz)</span></label>
                  <select 
                    className="form-select" 
                    value={formData.proforma_id} 
                    onChange={(e) => {
                      const selectedProforma = proformas.find(p => p.id === e.target.value);
                      if (selectedProforma) {
                        // Proforma seçildiğinde tüm bilgileri otomatik doldur
                        // Vessel ID'yi bul (isimden)
                        const matchedVessel = vessels.find(v => 
                          v.name?.toLowerCase() === selectedProforma.vessel_name?.toLowerCase() ||
                          v.id === selectedProforma.vessel_id
                        );
                        // Client ID'yi bul (isimden)
                        const matchedClient = clients.find(c => 
                          c.name?.toLowerCase() === selectedProforma.client_name?.toLowerCase() ||
                          c.id === selectedProforma.client_id
                        );
                        
                        setFormData({ 
                          ...formData, 
                          proforma_id: e.target.value,
                          pda_number: selectedProforma.number || selectedProforma.pda_number || '',
                          vessel_id: matchedVessel?.id || selectedProforma.vessel_id || '',
                          vessel_name: selectedProforma.vessel_name || matchedVessel?.name || '',
                          client_id: matchedClient?.id || selectedProforma.client_id || '',
                          client_name: selectedProforma.client_name || matchedClient?.name || ''
                        });
                      } else {
                        setFormData({ ...formData, proforma_id: e.target.value });
                      }
                    }} 
                    data-testid="proforma-select"
                  >
                    <option value="">Proforma Seçin (Opsiyonel)</option>
                    {proformas
                      .filter(p => p.status === 'approved' || p.status === 'sent' || p.status === 'converted')
                      .map(proforma => (
                        <option key={proforma.id} value={proforma.id}>
                          {proforma.number} - {proforma.vessel_name} ({proforma.client_name})
                        </option>
                      ))}
                  </select>
                  {formData.proforma_id && (
                    <p style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>
                      ✓ PDA No: {formData.pda_number || 'Yok'} | Gemi ve müşteri bilgileri otomatik dolduruldu
                    </p>
                  )}
                  {proformas.filter(p => p.status === 'approved' || p.status === 'sent' || p.status === 'converted').length === 0 && (
                    <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                      Henüz uygun proforma yok. Proforma seçmeden de liman çağrısı oluşturabilirsiniz.
                    </p>
                  )}
                </div>
                
                {/* Geldiği Liman (Origin Port) - Yeni Aranabilir Dropdown */}
                <div className="form-row">
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label">Geldiği Liman</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-input"
                        value={originPortSearch !== '' ? originPortSearch : formData.origin_port || ''}
                        onChange={(e) => {
                          setOriginPortSearch(e.target.value);
                          setShowOriginPortDropdown(true);
                        }}
                        onFocus={() => {
                          setOriginPortSearch(formData.origin_port || '');
                          setShowOriginPortDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowOriginPortDropdown(false), 200)}
                        placeholder="Liman kodu veya adı yazın..."
                        data-testid="origin-port-select"
                      />
                      {showOriginPortDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          maxHeight: '250px',
                          overflowY: 'auto',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1000
                        }}>
                          {ports
                            .filter(p => {
                              const search = (originPortSearch || '').toLowerCase();
                              return search === '' || 
                                p.code.toLowerCase().includes(search) || 
                                p.name.toLowerCase().includes(search);
                            })
                            .slice(0, 50)
                            .map(port => (
                              <div
                                key={port.code}
                                onClick={() => {
                                  setFormData({ ...formData, origin_port: `${port.name} (${port.code})`, origin_port_code: port.code });
                                  setOriginPortSearch('');
                                  setShowOriginPortDropdown(false);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  background: formData.origin_port_code === port.code ? '#eff6ff' : 'transparent'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = formData.origin_port_code === port.code ? '#eff6ff' : 'transparent'}
                              >
                                <span style={{ fontWeight: 600, color: port.code.startsWith('TR') ? '#16a34a' : '#3b82f6' }}>{port.code}</span>
                                <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>{port.name}</span>
                              </div>
                            ))}
                          {ports.filter(p => {
                            const search = (originPortSearch || '').toLowerCase();
                            return search === '' || p.code.toLowerCase().includes(search) || p.name.toLowerCase().includes(search);
                          }).length === 0 && (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>
                              Liman bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Gideceği Liman (Destination Port) */}
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label">Gideceği Liman</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-input"
                        value={destPortSearch !== '' ? destPortSearch : formData.destination_port || ''}
                        onChange={(e) => {
                          setDestPortSearch(e.target.value);
                          setShowDestPortDropdown(true);
                        }}
                        onFocus={() => {
                          setDestPortSearch(formData.destination_port || '');
                          setShowDestPortDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowDestPortDropdown(false), 200)}
                        placeholder="Liman kodu veya adı yazın..."
                        data-testid="dest-port-select"
                      />
                      {showDestPortDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          maxHeight: '250px',
                          overflowY: 'auto',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1000
                        }}>
                          {ports
                            .filter(p => {
                              const search = (destPortSearch || '').toLowerCase();
                              return search === '' || 
                                p.code.toLowerCase().includes(search) || 
                                p.name.toLowerCase().includes(search);
                            })
                            .slice(0, 50)
                            .map(port => (
                              <div
                                key={port.code}
                                onClick={() => {
                                  setFormData({ ...formData, destination_port: `${port.name} (${port.code})`, destination_port_code: port.code });
                                  setDestPortSearch('');
                                  setShowDestPortDropdown(false);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  background: formData.destination_port_code === port.code ? '#eff6ff' : 'transparent'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = formData.destination_port_code === port.code ? '#eff6ff' : 'transparent'}
                              >
                                <span style={{ fontWeight: 600, color: port.code.startsWith('TR') ? '#16a34a' : '#3b82f6' }}>{port.code}</span>
                                <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>{port.name}</span>
                              </div>
                            ))}
                          {ports.filter(p => {
                            const search = (destPortSearch || '').toLowerCase();
                            return search === '' || p.code.toLowerCase().includes(search) || p.name.toLowerCase().includes(search);
                          }).length === 0 && (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>
                              Liman bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Konum Tipi *</label>
                    <div style={{ 
                      display: 'flex', 
                      gap: '1rem', 
                      marginTop: '0.5rem' 
                    }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        padding: '0.75rem 1.25rem',
                        border: formData.location_type === 'anchorage' ? '2px solid #f59e0b' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: formData.location_type === 'anchorage' ? '#fffbeb' : '#fff',
                        fontWeight: formData.location_type === 'anchorage' ? 600 : 400,
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="radio"
                          name="location_type"
                          value="anchorage"
                          checked={formData.location_type === 'anchorage'}
                          onChange={(e) => setFormData({ ...formData, location_type: e.target.value, berth: '' })}
                          style={{ display: 'none' }}
                        />
                        <Anchor size={18} color={formData.location_type === 'anchorage' ? '#f59e0b' : '#64748b'} />
                        Demirsahası
                      </label>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        padding: '0.75rem 1.25rem',
                        border: formData.location_type === 'berth' ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: formData.location_type === 'berth' ? '#eff6ff' : '#fff',
                        fontWeight: formData.location_type === 'berth' ? 600 : 400,
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="radio"
                          name="location_type"
                          value="berth"
                          checked={formData.location_type === 'berth'}
                          onChange={(e) => setFormData({ ...formData, location_type: e.target.value, berth: '' })}
                          style={{ display: 'none' }}
                        />
                        <Anchor size={18} color={formData.location_type === 'berth' ? '#3b82f6' : '#64748b'} />
                        Rıhtım
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      {formData.location_type === 'anchorage' ? 'Demirsahası Bölgesi' : 'Rıhtım'}
                    </label>
                    {formData.location_type === 'anchorage' ? (
                      <select
                        className="form-select"
                        value={formData.berth}
                        onChange={(e) => setFormData({ ...formData, berth: e.target.value })}
                        data-testid="berth-select"
                      >
                        <option value="">Demirsahası Seçin (Opsiyonel)</option>
                        {anchorageOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <input 
                        type="text" 
                        className="form-input" 
                        value={formData.berth} 
                        onChange={(e) => setFormData({ ...formData, berth: e.target.value })} 
                        placeholder="Örn: Rıhtım 12" 
                        data-testid="berth-input" 
                      />
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">ETA</label>
                    <input type="datetime-local" className="form-input" value={formData.eta} onChange={(e) => setFormData({ ...formData, eta: e.target.value })} data-testid="eta-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ETD</label>
                    <input type="datetime-local" className="form-input" value={formData.etd} onChange={(e) => setFormData({ ...formData, etd: e.target.value })} data-testid="etd-input" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea className="form-input" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" data-testid="notes-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>İptal</button>
                <button type="submit" className="btn btn-primary" data-testid="save-portcall-btn">Liman Çağrısı Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetail && selectedPortCall && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="detail-title">Liman Çağrısı {selectedPortCall.number}</h2>
              <button className="modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--primary)' }}>Status</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <span className={`badge ${getStatusBadgeClass(selectedPortCall.status)}`} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                    {selectedPortCall.status.replace('_', ' ').toUpperCase()}
                  </span>
                  {getNextStatus(selectedPortCall.status) && (
                    <button className="btn btn-sm btn-primary" onClick={() => handleStatusUpdate(getNextStatus(selectedPortCall.status))} data-testid="update-status-btn">
                      Move to {getNextStatus(selectedPortCall.status).replace('_', ' ')}
                    </button>
                  )}
                  {getPreviousStatus(selectedPortCall.status) && (
                    <button 
                      className="btn btn-sm" 
                      style={{ 
                        background: '#f59e0b', 
                        color: 'white',
                        border: 'none'
                      }} 
                      onClick={() => {
                        if (window.confirm(`Durumu "${getStatusLabel(getPreviousStatus(selectedPortCall.status))}" olarak geri almak istediğinize emin misiniz?`)) {
                          handleStatusUpdate(getPreviousStatus(selectedPortCall.status));
                        }
                      }} 
                      data-testid="revert-status-btn"
                    >
                      ↩ Geri Al ({getStatusLabel(getPreviousStatus(selectedPortCall.status))})
                    </button>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--primary)' }}>Operational Checklist</h4>
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem' }}>
                  {selectedPortCall.checklist.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', borderBottom: idx < selectedPortCall.checklist.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }} onClick={() => toggleChecklistItem(idx)} data-testid={`checklist-item-${idx}`}>
                      {item.completed ? <CheckCircle size={20} className="text-success" /> : <Circle size={20} style={{ color: '#cbd5e1' }} />}
                      <span style={{ flex: 1, textDecoration: item.completed ? 'line-through' : 'none', color: item.completed ? '#64748b' : 'inherit' }}>{item.task}</span>
                      {item.completed_at && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{new Date(item.completed_at).toLocaleDateString()}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetail(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Port Call Modal */}
      {showEditModal && editingPortCall && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title">Liman Çağrısı Düzenle</h2>
              <button className="modal-close" onClick={() => { setShowEditModal(false); setEditingPortCall(null); }}>×</button>
            </div>
            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                {/* Gemi ve Müşteri Bilgileri (salt okunur) */}
                <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Gemi:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingPortCall.vessel_name}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Müşteri:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingPortCall.client_name}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Numara:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingPortCall.number}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Durum:</span>
                      <span className={`badge ${getStatusBadgeClass(editingPortCall.status)}`} style={{ marginLeft: '0.5rem' }}>
                        {editingPortCall.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* PDA Numarası */}
                <div className="form-group">
                  <label className="form-label">PDA Numarası</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editFormData.pda_number}
                    onChange={(e) => setEditFormData({ ...editFormData, pda_number: e.target.value })}
                    placeholder="PDA numarası girin"
                  />
                </div>

                {/* Konum Tipi */}
                <div className="form-group">
                  <label className="form-label">Konum Tipi</label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="edit_location_type"
                        value="berth"
                        checked={editFormData.location_type === 'berth'}
                        onChange={(e) => setEditFormData({ ...editFormData, location_type: e.target.value })}
                      />
                      <span>Rıhtım</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="edit_location_type"
                        value="anchorage"
                        checked={editFormData.location_type === 'anchorage'}
                        onChange={(e) => setEditFormData({ ...editFormData, location_type: e.target.value })}
                      />
                      <span>Demirsahası</span>
                    </label>
                  </div>
                </div>

                {/* Rıhtım/Demirsahası Adı */}
                <div className="form-group">
                  <label className="form-label">
                    {editFormData.location_type === 'anchorage' ? 'Demirsahası' : 'Rıhtım'} Adı
                  </label>
                  {editFormData.location_type === 'anchorage' ? (
                    <select
                      className="form-input"
                      value={editFormData.berth}
                      onChange={(e) => setEditFormData({ ...editFormData, berth: e.target.value })}
                    >
                      <option value="">Demirsahası seçin...</option>
                      {anchorageOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.berth}
                      onChange={(e) => setEditFormData({ ...editFormData, berth: e.target.value })}
                      placeholder="Örn: Rıhtım 5"
                    />
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">ETA (Tahmini Varış)</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={editFormData.eta}
                      onChange={(e) => setEditFormData({ ...editFormData, eta: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ETD (Tahmini Kalkış)</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={editFormData.etd}
                      onChange={(e) => setEditFormData({ ...editFormData, etd: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea
                    className="form-input"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    rows="3"
                    placeholder="Ek notlar..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); setEditingPortCall(null); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortCalls;
